import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatDZ } from '../../../utils/currency';
import { useCart } from '../../../context/CartContext';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const featuredProducts = [
    {
      id: 1,
      name: "iPhone 15 Pro Max",
      price: 1199,
      originalPrice: 1299,
      image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=500&h=500&fit=crop",
      badge: "New Arrival",
      rating: 4.9,
      reviews: 2847
    },
    {
      id: 2,
      name: "MacBook Pro M3",
      price: 1999,
      originalPrice: 2199,
      image: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=500&h=500&fit=crop",
      badge: "Best Seller",
      rating: 4.8,
      reviews: 1923
    },
    {
      id: 3,
      name: "AirPods Pro 2nd Gen",
      price: 249,
      originalPrice: 279,
      image: "https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=500&h=500&fit=crop",
      badge: "Limited Offer",
      rating: 4.7,
      reviews: 3456
    }
  ];

  const [currentProduct, setCurrentProduct] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProduct((prev) => (prev + 1) % featuredProducts?.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [featuredProducts?.length]);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-background to-card overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-accent/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>
      <div className="relative z-10 container mx-auto px-6 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Brand Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-2"
            >
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <span className="text-sm font-mono text-primary">TECH COMMAND CENTER</span>
            </motion.div>

            {/* Main Heading */}
            <div className="space-y-4">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl lg:text-7xl font-bold leading-tight"
              >
                <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  Premium
                </span>
                <br />
                <span className="text-foreground">Electronics</span>
                <br />
                <span className="command-text">Made Accessible</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-muted-foreground max-w-lg leading-relaxed"
              >
                Discover cutting-edge technology with transparent pricing, real-time inventory, and premium customer experience. Your trusted destination for professional-grade electronics.
              </motion.p>
            </div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/category-pages">
                <Button
                  variant="default"
                  size="lg"
                  iconName="ShoppingBag"
                  iconPosition="right"
                  className="w-full sm:w-auto trust-seal"
                >
                  Explore Products
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Watch Demo
              </Button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-center space-x-6 pt-4"
            >
              <div className="flex items-center space-x-2">
                <div className="trust-seal p-2 bg-success/10 border border-success/30 rounded-lg">
                  <Icon name="Shield" size={20} color="var(--color-success)" />
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-foreground">SSL Secured</div>
                  <div className="text-muted-foreground">256-bit encryption</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="inventory-pulse high"></div>
                <div className="text-sm">
                  <div className="font-semibold text-foreground">Live Inventory</div>
                  <div className="text-muted-foreground">Real-time updates</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Product Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              {/* Main Product Display */}
              <motion.div
                key={currentProduct}
                initial={{ opacity: 0, scale: 0.8, rotateY: 90 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                exit={{ opacity: 0, scale: 0.8, rotateY: -90 }}
                transition={{ duration: 0.6 }}
                className="holographic-card p-8 rounded-2xl relative overflow-hidden"
              >
                {/* Product Badge */}
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-semibold">
                    {featuredProducts?.[currentProduct]?.badge}
                  </span>
                </div>

                {/* Product Image */}
                <div className="relative mb-6">
                  <div className="w-full h-80 rounded-xl overflow-hidden bg-gradient-to-br from-muted/20 to-muted/40">
                    <Image
                      src={featuredProducts?.[currentProduct]?.image}
                      alt={featuredProducts?.[currentProduct]?.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  
                  {/* Floating Action Button */}
                  <div className="absolute bottom-4 right-4">
                    <Button
                      variant="default"
                      size="icon"
                      className="rounded-full shadow-lg trust-seal"
                      onClick={() => { const p = featuredProducts[currentProduct]; addToCart(p, 1); navigate('/shopping-cart'); }}
                    >
                      <Icon name="ShoppingCart" size={20} />
                    </Button>
                  </div>
                </div>

                {/* Product Info */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {featuredProducts?.[currentProduct]?.name}
                    </h3>
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="flex items-center space-x-1">
                        {[...Array(5)]?.map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={16}
                            className={i < Math.floor(featuredProducts?.[currentProduct]?.rating) ? "text-accent fill-current" : "text-muted-foreground"}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {featuredProducts?.[currentProduct]?.rating} ({featuredProducts?.[currentProduct]?.reviews?.toLocaleString()} reviews)
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl font-bold text-primary">
                        {formatDZ(featuredProducts?.[currentProduct]?.price)}
                      </span>
                      <span className="text-lg text-muted-foreground line-through">
                        {formatDZ(featuredProducts?.[currentProduct]?.originalPrice)}
                      </span>
                    </div>
                    <div className="text-sm text-success font-semibold">
                      {`Save ${formatDZ(featuredProducts?.[currentProduct]?.originalPrice - featuredProducts?.[currentProduct]?.price)}`}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Product Navigation Dots */}
              <div className="flex justify-center space-x-2 mt-6">
                {featuredProducts?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProduct(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentProduct
                        ? 'bg-primary scale-125' :'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-sm text-muted-foreground font-mono">SCROLL TO EXPLORE</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="p-2 border border-border rounded-full"
          >
            <Icon name="ChevronDown" size={20} className="text-muted-foreground" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;